import {
  AlarmClock,
  AlarmClockOff,
  Ban,
  CalendarCheck,
  Check,
  EllipsisVertical,
  type LucideIcon,
  Pencil,
  Play,
  Send,
  UserRoundPen,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { formatCurrency, isTaskSnoozed, type TaskResponse } from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import { EditTaskDialog } from '@/frontend/features/tasks/edit-task-dialog'
import { TaskReassignmentForm } from '@/frontend/features/tasks/forms/task-reassignment.form'
import { SnoozeTaskDialog } from '@/frontend/features/tasks/snooze-task-dialog'
import { useUpdateTask } from '@/frontend/features/tasks/use-update-task'
import { useSendInvoicePaymentLink } from '@/frontend/hooks/api/patients/use-send-invoice-payment-link'

interface TaskAction {
  label: string
  icon: LucideIcon
  onClick: () => void
  disabled?: boolean
  /** The action that moves the task forward; rendered as the filled button. */
  primary?: boolean
  destructive?: boolean
}

/**
 * Actions for progressing, snoozing, editing, reassigning, or cancelling a task.
 * `menu` folds them into a kebab dropdown for compact surfaces; `buttons` lays
 * them out in a row for an expanded task.
 */
export function TaskActions({
  task,
  variant = 'menu',
}: {
  task: TaskResponse
  variant?: 'menu' | 'buttons'
}) {
  const { mutate, isPending } = useUpdateTask(task.id)
  const sendPaymentLink = useSendInvoicePaymentLink(task.patient?.id ?? '')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [reassignOpen, setReassignOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [snoozeOpen, setSnoozeOpen] = useState(false)

  if (task.status === 'completed' || task.status === 'cancelled') return null

  const isProcedureRequest = task.data.type === 'procedure_request'
  // System-generated tasks carry snapshots in `data`, so their content isn't editable
  // through the standard form.
  const canEdit = task.type !== 'wet_script_request' && !isProcedureRequest
  const invoiceId = task.data.type === 'procedure_request' ? task.data.stripeInvoiceId : null
  const fee = task.data.type === 'procedure_request' ? task.data.fee : null

  const resendPaymentLink = () => {
    if (!invoiceId) return
    sendPaymentLink.mutate(
      { invoiceId },
      {
        onSuccess: (data) => toast.success(`Payment link sent to ${data.email}`),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to send payment link'),
      }
    )
  }

  const actions: TaskAction[] = []
  if (isProcedureRequest) {
    if (task.status === 'open' && invoiceId) {
      actions.push({
        label: 'Resend payment link',
        icon: Send,
        onClick: resendPaymentLink,
        disabled: sendPaymentLink.isPending,
      })
    }
    if (task.status === 'in_progress') {
      actions.push({
        label: 'Mark booked',
        icon: CalendarCheck,
        onClick: () => mutate({ status: 'completed' }),
        primary: true,
      })
    }
  } else {
    if (task.status === 'open') {
      actions.push({
        label: 'Start',
        icon: Play,
        onClick: () => mutate({ status: 'in_progress' }),
        primary: true,
      })
    }
    if (task.status === 'in_progress') {
      actions.push({
        label: 'Done',
        icon: Check,
        onClick: () => mutate({ status: 'completed' }),
        primary: true,
      })
    }
  }
  if (isTaskSnoozed(task)) {
    actions.push({
      label: 'Unsnooze',
      icon: AlarmClockOff,
      onClick: () => mutate({ snoozedUntil: null }),
    })
  } else {
    actions.push({ label: 'Snooze…', icon: AlarmClock, onClick: () => setSnoozeOpen(true) })
  }
  if (canEdit) actions.push({ label: 'Edit', icon: Pencil, onClick: () => setEditOpen(true) })
  actions.push({ label: 'Reassign', icon: UserRoundPen, onClick: () => setReassignOpen(true) })
  actions.push({
    label: isProcedureRequest ? 'Cancel request' : 'Cancel task',
    icon: Ban,
    onClick: () => setConfirmOpen(true),
    destructive: true,
  })

  return (
    <>
      {variant === 'menu' ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<Button variant="ghost" className="h-7 w-7 p-0" disabled={isPending} />}
          >
            <EllipsisVertical className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {actions.map((action) => (
              <DropdownMenuItem
                key={action.label}
                onClick={action.onClick}
                disabled={action.disabled}
                className={
                  action.destructive ? 'text-destructive-fg focus:text-destructive-fg' : undefined
                }
              >
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {actions.map((action) => (
            <Button
              key={action.label}
              variant={
                action.destructive ? 'ghost-destructive' : action.primary ? 'default' : 'outline'
              }
              className={action.destructive ? 'ml-auto' : undefined}
              disabled={isPending || action.disabled}
              onClick={action.onClick}
            >
              <action.icon data-icon="inline-start" />
              {action.label}
            </Button>
          ))}
        </div>
      )}

      {canEdit && <EditTaskDialog task={task} open={editOpen} onOpenChange={setEditOpen} />}

      {/* Mounted on demand so each opening starts with a blank date. */}
      {snoozeOpen && <SnoozeTaskDialog task={task} open onOpenChange={setSnoozeOpen} />}

      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reassign task</DialogTitle>
            <DialogDescription>
              Choose a new assignee for &ldquo;{task.title}&rdquo;.
            </DialogDescription>
          </DialogHeader>
          <TaskReassignmentForm
            taskId={task.id}
            onSuccess={() => setReassignOpen(false)}
            onCancel={() => setReassignOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation: title and button read "Cancel request" for procedure requests */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent showCloseButton={false} className="max-w-xs">
          <DialogHeader>
            <DialogTitle>{isProcedureRequest ? 'Cancel request' : 'Cancel task'}</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel &ldquo;{task.title}&rdquo;? This cannot be undone.
              {fee &&
                ` The ${formatCurrency(fee.amount, fee.currency)} fee will be refunded to the member if paid, or its invoice voided.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Go back</DialogClose>
            <Button
              variant="destructive"
              disabled={isPending}
              onClick={() => {
                mutate({ status: 'cancelled' }, { onSuccess: () => setConfirmOpen(false) })
              }}
            >
              {isProcedureRequest ? 'Cancel request' : 'Cancel task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
